import * as React from 'react';
import { cn } from '@/lib/utils';

// shadcn-style Card primitives. The default surface is `bg-card/70` +
// `backdrop-blur-md` so cards layered over the dashboard's gradient mesh
// pick up the glassmorphic effect without each consumer having to set it.
// Override `className` for solid surfaces.

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // macOS-style vibrant glass surface. Three things make it read as
      // "glass" rather than "frosted plastic":
      //   1. High blur + saturate boosts colours behind the panel so the
      //      gradient backdrop bleeds through with vibrancy.
      //   2. Low-opacity background tint keeps the surface translucent;
      //      dark-mode uses a near-black with very low alpha so the
      //      gradient still reads through.
      //   3. A subtle inner highlight on the top edge (the ::before band)
      //      mimics how Sonoma / Sequoia panels catch light, separating the
      //      panel from the surface beneath without a hard border line.
      'relative overflow-hidden rounded-2xl text-card-foreground',
      'border border-white/45 bg-white/45 dark:border-white/10 dark:bg-white/5',
      'backdrop-blur-2xl backdrop-saturate-150',
      'shadow-[0_1px_2px_rgba(15,23,42,0.04),0_22px_60px_-20px_rgba(15,23,42,0.18)]',
      'dark:shadow-[0_1px_2px_rgba(0,0,0,0.4),0_22px_60px_-20px_rgba(0,0,0,0.55)]',
      'before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px',
      'before:bg-gradient-to-r before:from-white/0 before:via-white/70 before:to-white/0',
      'dark:before:via-white/15',
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col gap-1 p-5 pb-3', className)}
    {...props}
  />
));
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'text-base font-semibold leading-tight tracking-tight',
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-5 pt-2', className)} {...props} />
));
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-5 pt-3', className)}
    {...props}
  />
));
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter };
